import React, { useState, useEffect } from 'react';
import { Search, Plus, Eye, Edit, Trash2, LogOut, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { apiClient } from '../../utils/apiHelper';

const VisitorLog = () => {
  const [visitors, setVisitors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    visitor_name: '',
    phone: '',
    email: '',
    purpose: '',
    whom_to_meet: '',
    department: '',
    visit_date: new Date().toISOString().split('T')[0],
    in_time: new Date().toTimeString().slice(0, 5),
    out_time: '',
    id_type: '',
    id_number: '',
    address: '',
    status: 'In Campus'
  });

  useEffect(() => { fetchVisitors(); }, []);

  const fetchVisitors = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/frontdesk/visitors');
      setVisitors(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching visitors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/frontdesk/visitors', formData);
      alert('Visitor added successfully!');
      setShowModal(false);
      resetForm();
      fetchVisitors();
    } catch (error) {
      alert('Failed to add visitor');
    }
  };

  const handleCheckout = async (visitorId) => {
    if (!window.confirm('Mark visitor as checked out?')) return;
    try {
      await apiClient.put(`/api/frontdesk/visitors/${visitorId}/checkout`);
      fetchVisitors();
    } catch (error) {
      alert('Failed to checkout visitor');
    }
  };

  const handleDelete = async (visitorId) => {
    if (!window.confirm('Are you sure you want to delete this visitor record?')) return;
    try {
      await apiClient.delete(`/api/frontdesk/visitors/${visitorId}`);
      fetchVisitors();
    } catch (error) {
      alert('Failed to delete visitor');
    }
  };

  const resetForm = () => {
    setFormData({
      visitor_name: '',
      phone: '',
      email: '',
      purpose: '',
      whom_to_meet: '',
      department: '',
      visit_date: new Date().toISOString().split('T')[0],
      in_time: new Date().toTimeString().slice(0, 5),
      out_time: '',
      id_type: '',
      id_number: '',
      address: '',
      status: 'In Campus'
    });
  };

  const filteredVisitors = visitors.filter(v =>
    v.visitor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.phone?.includes(searchTerm) ||
    v.purpose?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Visitor Log</CardTitle>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="h-4 w-4 mr-2" /> Add Visitor
          </Button>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search by name, phone, or purpose..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Visitor Name</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Purpose</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Whom to Meet</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">In Time</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Out Time</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredVisitors.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center p-8 text-gray-500">
                      {loading ? 'Loading...' : 'No visitor records found. Click "Add Visitor" to create a new entry.'}
                    </td>
                  </tr>
                ) : (
                  filteredVisitors.map((visitor) => (
                    <tr key={visitor.id} className="hover:bg-gray-50">
                      <td className="p-3 text-sm">{visitor.visit_date}</td>
                      <td className="p-3 text-sm font-medium">{visitor.visitor_name}</td>
                      <td className="p-3 text-sm">{visitor.phone}</td>
                      <td className="p-3 text-sm">{visitor.purpose}</td>
                      <td className="p-3 text-sm">{visitor.whom_to_meet || '-'}</td>
                      <td className="p-3 text-sm">{visitor.in_time}</td>
                      <td className="p-3 text-sm">{visitor.out_time || '-'}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          visitor.status === 'Checked Out' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {visitor.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          {visitor.status !== 'Checked Out' && (
                            <button onClick={() => handleCheckout(visitor.id)} className="text-orange-600 hover:text-orange-800" title="Checkout">
                              <LogOut className="h-4 w-4" />
                            </button>
                          )}
                          <button onClick={() => handleDelete(visitor.id)} className="text-red-600 hover:text-red-800" title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add Visitor Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add New Visitor</h2>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Visitor Name *</Label>
                  <Input required value={formData.visitor_name} onChange={(e) => setFormData({...formData, visitor_name: e.target.value})} />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                </div>
                <div>
                  <Label>Purpose *</Label>
                  <Input required value={formData.purpose} onChange={(e) => setFormData({...formData, purpose: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Whom to Meet</Label>
                  <Input value={formData.whom_to_meet} onChange={(e) => setFormData({...formData, whom_to_meet: e.target.value})} />
                </div>
                <div>
                  <Label>Department</Label>
                  <Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Visit Date *</Label>
                  <Input type="date" required value={formData.visit_date} onChange={(e) => setFormData({...formData, visit_date: e.target.value})} />
                </div>
                <div>
                  <Label>In Time *</Label>
                  <Input type="time" required value={formData.in_time} onChange={(e) => setFormData({...formData, in_time: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>ID Type</Label>
                  <Select value={formData.id_type} onValueChange={(v) => setFormData({...formData, id_type: v})}>
                    <SelectTrigger><SelectValue placeholder="Select ID Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aadhar">Aadhar Card</SelectItem>
                      <SelectItem value="PAN">PAN Card</SelectItem>
                      <SelectItem value="Driving License">Driving License</SelectItem>
                      <SelectItem value="Voter ID">Voter ID</SelectItem>
                      <SelectItem value="Passport">Passport</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>ID Number</Label>
                  <Input value={formData.id_number} onChange={(e) => setFormData({...formData, id_number: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit">Add Visitor</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorLog;
